import type { CopilotStatefulMessage } from '@customerio/hydra-copilot';

export type GroupedUserMessage = {
  role: 'user';
  type: 'grouped';
  text?: string;
  contentMessages: CopilotStatefulMessage[];
  // Preserve other properties from the base message
  [key: string]: unknown;
};

export type ProcessedMessage = CopilotStatefulMessage | GroupedUserMessage;

/**
 * Group consecutive user messages (text and content) into a single message bubble.
 * This improves the visual display by combining text and file uploads that were sent together.
 */
export function groupUserMessages(messages: CopilotStatefulMessage[]): ProcessedMessage[] {
  const groupedMessages: ProcessedMessage[] = [];
  let index = 0;

  while (index < messages.length) {
    const message = messages[index];

    if (message.role === 'user') {
      // Collect all consecutive user messages
      const userMessages: CopilotStatefulMessage[] = [message];
      let index_ = index + 1;

      while (index_ < messages.length && messages[index_].role === 'user') {
        userMessages.push(messages[index_]);
        index_++;
      }

      // If we have multiple user messages, group them
      if (userMessages.length > 1) {
        const textMessage = userMessages.find((m) => m.type === 'text');
        const contentMessages = userMessages.filter((m) => m.type === 'content');

        // Create a grouped message with explicit type
        const groupedMessage: GroupedUserMessage = {
          ...(textMessage ?? contentMessages[0]), // Preserve base message properties
          role: 'user',
          type: 'grouped',
          text: textMessage != null && 'text' in textMessage ? textMessage.text : undefined,
          contentMessages,
        };

        groupedMessages.push(groupedMessage);
      } else {
        // Single user message, add as-is
        groupedMessages.push(message);
      }

      index = index_; // Skip the messages we've already processed
    } else if (message.role === 'model' && message.type === 'tool_call') {
      // Collect all consecutive tool_call messages
      const toolCallMessages: CopilotStatefulMessage[] = [message];
      let index_ = index + 1;

      while (index_ < messages.length && messages[index_].role === 'model' && messages[index_].type === 'tool_call') {
        toolCallMessages.push(messages[index_]);
        index_++;
      }

      if (toolCallMessages.length > 1) {
        // Merge all tool_calls arrays into the first message, filtering out
        // post-approval execution tool calls (was_approved) since they are
        // redundant with the original approval tool call that's already shown.
        const allToolCalls = toolCallMessages.flatMap((m) => ('tool_calls' in m ? m.tool_calls : []));
        const filteredToolCalls = allToolCalls.filter((tc) => tc.was_approved !== true);

        // If all tool calls were filtered out, skip the entire group
        if (filteredToolCalls.length > 0) {
          const merged = {
            ...toolCallMessages[0],
            tool_calls: filteredToolCalls,
          };
          groupedMessages.push(merged as CopilotStatefulMessage);
        }
      } else {
        // Single tool_call message — still filter out was_approved tool calls
        const tc = message as CopilotStatefulMessage & { type: 'tool_call' };
        const filteredToolCalls = tc.tool_calls.filter((t) => t.was_approved !== true);

        if (filteredToolCalls.length > 0) {
          if (filteredToolCalls.length === tc.tool_calls.length) {
            // Nothing was filtered, add original as-is
            groupedMessages.push(message);
          } else {
            groupedMessages.push({
              ...tc,
              tool_calls: filteredToolCalls,
            });
          }
        }
      }

      index = index_;
    } else {
      // Non-user message, add as-is
      groupedMessages.push(message);
      index++;
    }
  }

  return groupedMessages;
}
